import * as wjcCore from '@mescius/wijmo';
import * as wjcGrid from '@mescius/wijmo.grid';
import {ViewChild, Component, OnInit} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {ToasterService} from 'angular2-toaster';
import {DataService, OrientDBCollectionView, OUser} from '@cosbeni/database';
import {ActivePromotionConfig} from '../config/active-promotion.config';
import {KobeacService} from './kobeac.service';
import {ROLES} from '../services/roles';
import {TranslocoService} from '@jsverse/transloco';

/**
 * kobe-ac ユーザ管理 (旧「会員管理」を拡張)。神戸オークションラインの OUser
 * (神戸オークションユーザ / 神戸オークション管理者) を作成・編集・パスワード変更し、
 * 併せて OUser に AC_Customer を紐付ける (紐付けたユーザは書類画面で自分の顧客
 * フォルダ=売買台帳PDF 等のみ閲覧可)。UI はオンラインオークションのユーザ管理に合わせ、
 * 左に一覧グリッド・右に作成/編集フォーム (mode: view/edit/confirm) を置く。
 * 割当可能ロールは神戸ラインの 2 つに限定し、一覧も神戸ロール保有ユーザのみを表示する。
 *
 * 新規ユーザ登録 (issue #255): 公開ページ /#/register/kobe からの申請は registrationStatus=PENDING の
 * OUser (SUSPENDED) として一覧に載る。管理者は申請内容を見て顧客を紐付けて承認 (ACTIVE 化 +
 * パスワード設定メール) するか却下する。登録ページの QR コードとプライバシーポリシー・通知先の設定もここで扱う。
 */
@Component({
    standalone: false,
    selector: 'kobeac-member',
    templateUrl: 'member.component.html',
})
export class KobeacMemberComponent implements OnInit {
    readonly: Boolean = true;
    @ViewChild('grid') grid: wjcGrid.FlexGrid;
    users: OrientDBCollectionView;
    /** AC_Customer 一覧 (顧客紐付け用)。 */
    customers: any[] = [];
    /** 割当可能ロール (神戸オークションユーザ / 神戸オークション管理者) のみ。 */
    assignableRoles: any[] = [];
    private allRoles: any[] = [];
    user: any = new OUser();
    selectedRoleName: string = ROLES.KOBE_USER;
    selectedCustomerRid: string = '';
    password: string = '';
    confirmPassword: string = '';
    newPassword: string = '';
    confirmNewPassword: string = '';
    mode: string = 'view';

    /** 新規登録 (issue #255) */
    private readonly registrationApi = ActivePromotionConfig.SERVER_URL + '/rest/kobeac/registration';
    private readonly baseScope: string;
    pendingOnly = false;
    pendingCount = 0;
    approveCustomerRid = '';
    rejectConfirm = false;
    processing = false;
    /** 右側の補助パネル: 登録用 QR コード / 新規登録の設定 */
    panel: '' | 'qr' | 'settings' = '';
    @ViewChild('qr') qr: any;
    registrationSettings = {policy: '', notifyTo: ''};

    /** この画面が扱う神戸ラインのロール名。 */
    private readonly kobeRoleNames: string[] = [ROLES.KOBE_USER, ROLES.KOBE_ADMIN];

    constructor(public dataService: DataService, private kobeac: KobeacService, public toasterService: ToasterService,
                private transloco: TranslocoService, private http: HttpClient) {
        const scope = "roles CONTAINS (name IN ['" + ROLES.KOBE_USER + "','" + ROLES.KOBE_ADMIN + "'])";
        this.users = new OrientDBCollectionView(dataService, ActivePromotionConfig.DATABASE, 'SELECT FROM OUser', 'OUser', '', 0, 100, true, true, false);
        this.users.url = ActivePromotionConfig.SERVER_URL;
        this.users.tokenName = ActivePromotionConfig.DATABASE + '_token';
        this.baseScope = scope;
        this.users.filterSpecial = scope;
        this.users.filterOnServer = true;
    }

    async ngOnInit(): Promise<void> {
        this.users.currentChanged.addHandler(this.onCurrentChanged, this);
        await this.kobeac.ensureLoaded();
        this.customers = this.kobeac.customers;
        this.dataService.query({sql: 'SELECT FROM ORole', count: 'SELECT FROM ORole'}, ActivePromotionConfig.SERVER_URL).subscribe((res: any) => {
            this.allRoles = wjcCore.isArray(res.results) ? res.results : [];
            this.assignableRoles = this.allRoles.filter((r) => this.kobeRoleNames.indexOf(r.name) >= 0);
        });
        this.loadPendingCount();
    }

    onCurrentChanged(sender: any, args: wjcCore.EventArgs): void {
        if (this.users.currentItem != null) {
            this.user = Object.assign({}, this.users.currentItem);
            this.selectedRoleName = this.kobeRoleOf(this.user.roles);
            this.selectedCustomerRid = this.customerRidOf(this.user.customer);
            this.mode = 'view';
            this.approveCustomerRid = '';
            this.rejectConfirm = false;
        }
    }

    /** OUser.roles から神戸ラインのロール名を取り出す (無ければ神戸ユーザ既定)。 */
    private kobeRoleOf(roles: any): string {
        if (wjcCore.isArray(roles)) {
            for (const r of roles) {
                const name = wjcCore.isObject(r) ? r.name : r;
                if (this.kobeRoleNames.indexOf(name) >= 0) {
                    return name;
                }
            }
        }
        return ROLES.KOBE_USER;
    }

    /** OUser.customer (link or rid) から @rid 文字列を得る。 */
    private customerRidOf(customer: any): string {
        if (wjcCore.isObject(customer)) {
            return customer['@rid'] || '';
        }
        if (wjcCore.isString(customer)) {
            return customer;
        }
        return '';
    }

    private roleObj(name: string): any {
        for (const r of this.allRoles) {
            if (r.name === name) {
                return r;
            }
        }
        return null;
    }

    private roleRid(name: string): string {
        const r = this.roleObj(name);
        return r ? r['@rid'] : '';
    }

    /** グリッドの顧客列に表示するラベル (ID + 名称)。 */
    customerLabelOf(item: any): string {
        const rid = this.customerRidOf(item ? item.customer : null);
        if (!rid) {
            return '';
        }
        for (const c of this.customers) {
            if (c['@rid'] === rid) {
                return c['ID'] + ' ' + c['名称'];
            }
        }
        return '';
    }

    customerLabel(c: any): string {
        return c['ID'] + ' ' + c['名称'];
    }

    get isNew(): boolean {
        return this.user && this.user['@rid'] == '#-1:-1';
    }

    /** email は空 (任意) か、メールアドレスの形になっていれば OK。 */
    get emailValid(): boolean {
        const e = (this.user && this.user.email ? String(this.user.email) : '').trim();
        return !e || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);
    }

    get createValid(): boolean {
        return !wjcCore.isNullOrWhiteSpace(this.user.name) && this.user.name.length >= 3
            && !wjcCore.isNullOrWhiteSpace(this.password) && this.password.length >= 3
            && this.password === this.confirmPassword
            && !wjcCore.isNullOrWhiteSpace(this.selectedRoleName)
            && this.emailValid;
    }

    get editValid(): boolean {
        return !wjcCore.isNullOrWhiteSpace(this.user.name) && this.user.name.length >= 3
            && !wjcCore.isNullOrWhiteSpace(this.selectedRoleName)
            && this.emailValid;
    }

    get passwordValid(): boolean {
        return !wjcCore.isNullOrWhiteSpace(this.newPassword) && this.newPassword.length >= 3
            && this.newPassword === this.confirmNewPassword;
    }

    editUser(): void {
        this.mode = 'edit';
    }

    confirmUser(): void {
        this.mode = 'confirm';
    }

    createUser(): void {
        this.mode = 'edit';
        this.user = new OUser();
        this.user.status = 'ACTIVE';
        this.selectedRoleName = ROLES.KOBE_USER;
        this.selectedCustomerRid = '';
        this.password = '';
        this.confirmPassword = '';
    }

    /** ユーザ (名前/ステータス/ロール/顧客リンク) を保存する。LINK は SQL で設定する。 */
    saveUser(): void {
        const roleRid = this.roleRid(this.selectedRoleName);
        const customer = this.selectedCustomerRid ? this.selectedCustomerRid : 'null';
        const props: string[] = [];
        props.push("name = '" + this.escape(this.user.name) + "'");
        props.push("status = '" + this.escape(this.user.status || 'ACTIVE') + "'");
        props.push("email = '" + this.escape((this.user.email || '').trim()) + "'");
        if (roleRid) {
            props.push('roles = [' + roleRid + ']');
        }
        props.push('customer = ' + customer);
        let sql = 'BEGIN\n';
        if (this.isNew) {
            props.push("password = '" + this.escape(this.password) + "'");
            sql = sql + 'INSERT INTO OUser SET ' + props.join(', ') + '\n';
        } else {
            sql = sql + 'UPDATE OUser SET ' + props.join(', ') + ' WHERE @rid = ' + this.user['@rid'] + '\n';
        }
        sql = sql + 'COMMIT RETRY 100\n';
        this.dataService.batch({sql: sql}, ActivePromotionConfig.SERVER_URL).subscribe(() => {
            this.mode = 'view';
            this.toasterService.pop('info', this.transloco.translate('common.save'), this.transloco.translate('kobeac.saved_passive_period'));
            this.users.queryData();
        }, () => {
            this.toasterService.pop('error', this.transloco.translate('common.save'), this.transloco.translate('kobeac.failed_period'));
        });
    }

    changePassword(): void {
        const command = 'UPDATE ' + this.user['@rid'] + " SET password = '" + this.escape(this.newPassword) + "'";
        this.dataService.command({sql: command}, ActivePromotionConfig.SERVER_URL).subscribe(() => {
            this.mode = 'view';
            this.newPassword = '';
            this.confirmNewPassword = '';
            this.toasterService.pop('info', this.transloco.translate('kobeac.change_password'), this.transloco.translate('kobeac.changed_period'));
            this.users.queryData();
        }, () => {
            this.toasterService.pop('error', this.transloco.translate('kobeac.change_password'), this.transloco.translate('kobeac.failed_period'));
        });
    }

    // ---- 新規ユーザ登録 (issue #255) ----

    get isPending(): boolean {
        return this.user && this.user.registrationStatus === 'PENDING';
    }

    /** 一覧の状態列: 申請中・却下は registrationStatus を優先して見せる */
    statusLabelOf(item: any): string {
        if (item && item.registrationStatus === 'PENDING') {
            return 'PENDING';
        }
        if (item && item.registrationStatus === 'REJECTED') {
            return this.transloco.translate('kobeac.registration_rejected');
        }
        return item ? item.status : '';
    }

    /** 申請日時などの表示用 */
    formatDate(v: any): string {
        if (!v) {
            return '';
        }
        const d = wjcCore.isDate(v) ? v : new Date(String(v).replace(' ', 'T'));
        return isNaN(d.getTime()) ? String(v) : wjcCore.Globalize.format(d, 'yyyy/MM/dd HH:mm');
    }

    loadPendingCount(): void {
        const sql = "SELECT count(*) as n FROM OUser WHERE registrationStatus = 'PENDING'";
        this.dataService.query({sql: sql, count: sql}, ActivePromotionConfig.SERVER_URL).subscribe({
            next: (res: any) => {
                const r = wjcCore.isArray(res.results) && res.results.length ? res.results[0] : null;
                this.pendingCount = r && r.n != null ? Number(r.n) : 0;
            },
            error: () => this.pendingCount = 0,
        });
    }

    /** 承認待ちだけに絞る / 戻す */
    togglePendingOnly(): void {
        this.users.filterSpecial = this.pendingOnly
            ? this.baseScope + " AND registrationStatus = 'PENDING'"
            : this.baseScope;
        this.users.queryData();
    }

    private reloadAfterRegistration(): void {
        this.users.queryData();
        this.loadPendingCount();
    }

    approve(): void {
        if (!this.approveCustomerRid || this.processing) {
            return;
        }
        this.processing = true;
        this.http.post(this.registrationApi + '/approve', {user: this.user['@rid'], customer: this.approveCustomerRid}).subscribe({
            next: (res: any) => {
                this.processing = false;
                if (res && res.mailSent) {
                    this.toasterService.pop('success', this.transloco.translate('kobeac.registration_approve'),
                        this.transloco.translate('kobeac.registration_approved_mail_sent'));
                } else {
                    this.toasterService.pop('warning', this.transloco.translate('kobeac.registration_approve'),
                        this.transloco.translate('kobeac.registration_approved_mail_failed') + (res && res.mailError ? ' (' + res.mailError + ')' : ''));
                }
                this.reloadAfterRegistration();
            },
            error: (e: any) => {
                this.processing = false;
                this.toasterService.pop('error', this.transloco.translate('kobeac.registration_approve'), this.errorMessage(e));
            },
        });
    }

    /** 却下は 2 回押し (1 回目で確認表示) */
    reject(): void {
        if (!this.rejectConfirm) {
            this.rejectConfirm = true;
            return;
        }
        this.processing = true;
        this.http.post(this.registrationApi + '/reject', {user: this.user['@rid']}).subscribe({
            next: () => {
                this.processing = false;
                this.rejectConfirm = false;
                this.toasterService.pop('info', this.transloco.translate('kobeac.registration_reject'),
                    this.transloco.translate('kobeac.registration_rejected_done'));
                this.reloadAfterRegistration();
            },
            error: (e: any) => {
                this.processing = false;
                this.toasterService.pop('error', this.transloco.translate('kobeac.registration_reject'), this.errorMessage(e));
            },
        });
    }

    private errorMessage(e: any): string {
        return e && e.error && e.error.message ? e.error.message : this.transloco.translate('kobeac.failed_period');
    }

    /** 登録ページの URL (QR コードの中身)。このアプリと同じホストの公開ページ */
    get registerUrl(): string {
        return window.location.origin + window.location.pathname + '#/register/kobe';
    }

    togglePanel(panel: 'qr' | 'settings'): void {
        this.panel = this.panel === panel ? '' : panel;
        if (this.panel === 'settings') {
            this.http.get(this.registrationApi + '/settings').subscribe({
                next: (res: any) => this.registrationSettings = {policy: res.policy || '', notifyTo: res.notifyTo || ''},
                error: (e: any) => this.toasterService.pop('error', this.transloco.translate('kobeac.registration_settings'), this.errorMessage(e)),
            });
        }
    }

    saveRegistrationSettings(): void {
        this.http.put(this.registrationApi + '/settings', this.registrationSettings).subscribe({
            next: () => this.toasterService.pop('info', this.transloco.translate('kobeac.registration_settings'),
                this.transloco.translate('kobeac.saved_passive_period')),
            error: (e: any) => this.toasterService.pop('error', this.transloco.translate('kobeac.registration_settings'), this.errorMessage(e)),
        });
    }

    private qrDataUrl(): string {
        const ctrl = this.qr && this.qr.getDataUrl ? this.qr : null;
        return ctrl ? ctrl.getDataUrl() : '';
    }

    downloadQr(): void {
        const url = this.qrDataUrl();
        if (!url) {
            return;
        }
        const a = document.createElement('a');
        a.href = url;
        a.download = 'kobe-register-qr.png';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    }

    /** QR コードと URL だけの印刷用ウィンドウを開いて印刷する */
    printQr(): void {
        const url = this.qrDataUrl();
        const w = url ? window.open('', '_blank') : null;
        if (!w) {
            return;
        }
        const title = this.transloco.translate('kobeac.registration_qr_print_title');
        const esc = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
        w.document.write('<!doctype html><html><head><meta charset="utf-8"><title>' + esc(title) + '</title></head>'
            + '<body style="text-align:center;font-family:sans-serif;padding-top:40px">'
            + '<h2>' + esc(title) + '</h2>'
            + '<img src="' + url + '" style="width:320px;height:320px;image-rendering:pixelated">'
            + '<p style="font-size:14px;word-break:break-all">' + esc(this.registerUrl) + '</p>'
            + '</body></html>');
        w.document.close();
        const img = w.document.images[0];
        const go = () => {
            w.focus();
            w.print();
        };
        if (img && !img.complete) {
            img.onload = go;
        } else {
            go();
        }
    }

    private escape(v: string): string {
        return String(v == null ? '' : v).replace(/\\/g, '\\\\').replace(/'/g, '\\\'');
    }
}
